import { useEffect, useRef, useState } from "react";

interface UseScrollSpyOptions {
  isScrollingRef?: React.MutableRefObject<boolean>;
}

export const useScrollSpy = (
  sectionIds: string[],
  options?: UseScrollSpyOptions
) => {
  const [activeSection, setActiveSection] = useState<string>(() => {
    // Initialize with first section
    return sectionIds[0];
  });

  // Use a ref to track active section so the IntersectionObserver callback
  // always has the current value (avoids stale closure issue).
  const activeSectionRef = useRef(activeSection);
  activeSectionRef.current = activeSection;

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const sectionElements = sectionIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (sectionElements.length === 0) return;

    // Track which sections are currently visible
    const visibleSections = new Set<string>();

    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        const sectionId = entry.target.id;

        if (entry.isIntersecting) {
          visibleSections.add(sectionId);
        } else {
          visibleSections.delete(sectionId);
        }
      });

      // Only update active section if not currently programmatically scrolling
      if (options?.isScrollingRef?.current === true) {
        return;
      }

      // If there are visible sections, activate the first one in the list
      if (visibleSections.size > 0) {
        const firstVisibleSection = sectionIds.find((id) =>
          visibleSections.has(id)
        );

        if (
          firstVisibleSection != null &&
          firstVisibleSection !== "" &&
          firstVisibleSection !== activeSectionRef.current
        ) {
          setActiveSection(firstVisibleSection);
        }
      }
    };

    // Create an observer for all sections
    const observer = new IntersectionObserver(observerCallback, {
      rootMargin: "-30% 0px -35% 0px", // Trigger when section is ~30% from top
      threshold: 0,
    });

    sectionElements.forEach((element) => {
      observer.observe(element);
    });
    observers.push(observer);

    return () => {
      observers.forEach((obs) => obs.disconnect());
    };
  }, [sectionIds, options?.isScrollingRef]);

  return activeSection;
};
